"use client";

import { Fragment, useState, useMemo, useEffect } from "react";
import {
  useShallowSearchParam,
  setShallowSearchParams,
} from "@/lib/hooks/use-shallow-search-param";
import Link from "next/link";
import type { LeaderboardEntry } from "@/lib/conditions/board";
import { boardEntriesAt } from "@/lib/conditions/board-freshness";
import { useBrowserMinuteClock } from "@/lib/hooks/use-minute-clock";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import { SKI_LABEL_CLOSED, skiLabel10 } from "@/lib/conditions/ski-labels-v3";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { weatherCodeEmoji, weatherCodeLabel } from "@/lib/conditions/weather-code-display";
import { PodiumSection } from "./PodiumSection";
import { TrendIndicator } from "./TrendIndicator";
import { SaveToCompanionButton } from "./SaveToCompanionButton";
import { companionSpotTypeForCategory } from "./save-to-companion-state";
import { callPreferenceCategoryForSport } from "./call-preference-options";
import {
  getContinentId,
  getContinentLabel,
  getAvailableContinents,
  getCountriesForEntries,
} from "@/lib/conditions/regions";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import {
  isNamedSupLeaderboardPromotionEligible,
  namedSupLeaderboardDisplayLabel,
  namedSupLeaderboardSafetyState,
} from "@/lib/conditions/sup-profile-leaderboard-presentation";
import {
  ScoreConfidenceBadge,
  scoreReadingWithConfidenceRange,
} from "./ScoreConfidenceBadge";
import { skiScoreConfidenceIsPublishable, type SkiScoreConfidenceMetadata } from "@/lib/conditions/ski-score-confidence";
import {
  hasSkiV3,
  isSkiWithheld,
  leaderboardSkiSport,
  SKI_SEASON_CLOSED_REASON,
  skiClosedReason,
  skiLikelyRange,
  skiScoreTooltip,
  skiWithheldLabel,
} from "./ski-board-display";
import { SkiClassChip, SkiExtentChip } from "./SkiScoreChips";
import { boardRowStatItems, boardStatsSport } from "./boards/row-stats";
import { filterByArea, knownBoardArea, type BoardAreaProps } from "./boards/board-area-model";
import { BoardAreaChips } from "./boards/BoardAreaChips";
import { BoardAreaFilter } from "./boards/BoardAreaFilter";
import { BoardNearYouChip } from "./boards/BoardNearYouChip";
import { BoardOffSeasonBanner } from "./boards/BoardOffSeasonBanner";
import { BoardWeekView } from "./boards/BoardWeekView";


export type LeaderboardCategory = "ski" | "surf" | "wind" | "foil" | "downwind-foil" | "wing-foil" | "diving" | "sup" | "sup-touring" | "sup-surf" | "sup-downwind" | "swim" | "mtb" | "touring" | "sailing" | "bodyboarding";
export type LeaderboardMode = "conditions" | "luckiest";
export type SkiSport = "piste" | "offpiste" | "touring";
export type SailingBoat = "club" | "hp";

function snowDepthConfidence(
  entry: LeaderboardEntry,
  category: LeaderboardCategory,
  skiSport: SkiSport,
): SkiScoreConfidenceMetadata | null {
  if (category === "touring") {
    return entry.snowDepthConfidenceBySport?.touring ?? null;
  }
  if (category !== "ski") return null;
  return skiSport === "offpiste"
    ? entry.snowDepthConfidenceBySport?.["off-piste"] ?? null
    : entry.snowDepthConfidenceBySport?.piste ?? null;
}

function scoreReading(
  score: number,
  confidence: SkiScoreConfidenceMetadata | null,
): string {
  return scoreReadingWithConfidenceRange(
    scoreOutOf100(score),
    confidence?.lo,
    confidence?.hi,
  );
}

function luckBadgeColor(delta: number | null): { bg: string; text: string } {
  if (delta === null) return { bg: "bg-slate-100", text: "text-text-muted" };
  if (delta >= 2.0) return { bg: "bg-emerald-500", text: "text-white" };
  if (delta >= 1.0) return { bg: "bg-lime-500", text: "text-white" };
  if (delta >= 0) return { bg: "bg-amber-500", text: "text-white" };
  if (delta >= -1.0) return { bg: "bg-orange-500", text: "text-white" };
  return { bg: "bg-red-500", text: "text-white" };
}

function MovementIndicator({
  rank,
  previousRank,
}: {
  rank: number;
  previousRank: number | null;
}) {
  if (previousRank === null) {
    return (
      <span
        className="text-[11px] text-text-muted/60 font-mono"
        aria-label="No previous rank"
      >
        new
      </span>
    );
  }
  const diff = previousRank - rank;
  if (diff === 0) {
    return (
      <span
        className="text-text-muted/70 text-sm font-mono"
        aria-label="No change"
      >
        ·
      </span>
    );
  }
  if (diff > 0) {
    return (
      <span
        className="text-emerald-600 text-[12px] font-semibold tabular-nums"
        aria-label={`Up ${diff} spots`}
      >
        ▲{diff}
      </span>
    );
  }
  return (
    <span
      className="text-red-600 text-[12px] font-semibold tabular-nums"
      aria-label={`Down ${Math.abs(diff)} spots`}
    >
      ▼{Math.abs(diff)}
    </span>
  );
}

function SurfKeyStats({ entry }: { entry: LeaderboardEntry }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-text-muted">
      <span>
        <strong className="text-navy font-semibold">
          {(entry.swellWaveHeightM ?? 0).toFixed(1)} m
        </strong>{" "}
        swell
      </span>
      <span>
        <strong className="text-navy font-semibold">
          {(entry.swellWavePeriodS ?? 0).toFixed(0)} s
        </strong>{" "}
        period
      </span>
      <span>
        <strong className="text-navy font-semibold">
          {(entry.rawWindSpeedKmh ?? 0).toFixed(0)} km/h
        </strong>{" "}
        wind
      </span>
      <span>
        <strong className="text-navy font-semibold">
          {entry.waterTempC !== null ? `${entry.waterTempC.toFixed(0)}°` : "–"}
        </strong>{" "}
        water
      </span>
    </div>
  );
}

/**
 * Sport-specific row stats for the generic boards (forecast areas spec, section 10.2): the stat
 * family comes from the shared rowStatsFor rule, so MTB, wind and diving rows never show swell.
 */
function BoardRowStats({ sport, entry }: { sport: string; entry: LeaderboardEntry }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-text-muted" data-row-stats={sport}>
      {boardRowStatItems(sport, entry).map((item) => (
        <span key={item.key}>
          <strong className="text-navy font-semibold">{item.value}</strong>{" "}
          {item.label}
        </span>
      ))}
    </div>
  );
}

function RowStats({
  category,
  skiSport,
  entry,
}: {
  category: LeaderboardCategory;
  skiSport: SkiSport;
  entry: LeaderboardEntry;
}) {
  const statsSport = boardStatsSport(category, skiSport);
  // Named paddle boards keep their own presentation.
  if (statsSport === null) return <SurfKeyStats entry={entry} />;
  return <BoardRowStats sport={statsSport} entry={entry} />;
}

/**
 * A ski resort whose score is withheld (Closed, or No lift access for off-piste): no number,
 * no rank, the reason when known. Grouped at the bottom of the board. The muted look comes
 * from the background only, so every word keeps AA contrast.
 */
function SkiWithheldRow({
  entry,
  href,
  label,
  reason: reasonOverride,
}: {
  entry: LeaderboardEntry;
  href: string;
  label: string;
  /** Reason when the row carries none of its own (a legacy closed-for-season row). */
  reason?: string;
}) {
  const reason = reasonOverride ?? skiClosedReason(entry);
  return (
    <div
      className="bg-white/60 rounded-[16px] border border-border-light"
      data-ski-withheld={label}
    >
      <Link
        href={href}
        className="flex-1 min-w-0 text-left p-4 md:p-5 flex items-center gap-3 md:gap-5 no-underline"
      >
        <div className="flex items-center gap-2 md:gap-3 w-14 md:w-20 shrink-0">
          <span
            className="font-serif text-[22px] md:text-[26px] font-bold text-text-muted/40 tabular-nums"
            aria-hidden
          >
            -
          </span>
          <span className="sr-only">Not ranked</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-serif text-[17px] md:text-[19px] font-semibold text-text-body leading-tight">
              {entry.name}
            </h3>
            <span className="text-[11px] text-text-muted">
              {entry.country}
            </span>
          </div>
          {reason && reason !== label && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-semibold text-slate-600">
                {reason}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center shrink-0">
          <div className="min-w-16 rounded-[10px] px-2.5 py-1.5 text-center bg-slate-100 border border-slate-200">
            <div className="text-[13px] md:text-[14px] font-semibold leading-tight text-text-body">
              {label}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

function LeaderboardRow({
  entry,
  category,
  mode,
  skiSport,
  sailingBoat = "club",
  basePath,
  selected,
  onSelect,
  disableCheckbox,
  theme = "default",
}: {
  entry: LeaderboardEntry;
  category: LeaderboardCategory;
  mode: LeaderboardMode;
  skiSport: SkiSport;
  sailingBoat?: SailingBoat;
  basePath: string;
  selected?: boolean;
  onSelect?: () => void;
  disableCheckbox?: boolean;
  theme?: LeaderboardTheme;
}) {
  const sport = boardLensSport(category, skiSport);
  // The lens rides in the fragment: crawlers only ever see the canonical spot URL.
  const entryHref = sport ? `${basePath}/${entry.slug}#sport=${sport}` : `${basePath}/${entry.slug}`;
  const displayScore = category === "sailing" && sailingBoat === "hp"
    ? (entry.sailingHpScore ?? entry.conditionsScore)
    : entry.conditionsScore;
  const conditionsBadge = scoreBadgeColor(displayScore);
  const alpineBadge = theme === "alpine" ? alpineScoreBadge(displayScore) : theme === "coastal" ? coastalScoreBadge(displayScore) : null;
  const saveSpotType = companionSpotTypeForCategory(category);
  const callPreference = {
    spotName: entry.name,
    category: callPreferenceCategoryForSport(category),
    allowedSupProfiles: isSupProfileKey(category) ? [category] : undefined,
  };
  const namedSafetyState = isSupProfileKey(category)
    ? namedSupLeaderboardSafetyState(entry)
    : null;
  const depthConfidence = snowDepthConfidence(entry, category, skiSport);
  // Ski boards: the entry's v3 fields describe the board's sport (piste, off-piste or touring).
  const skiDisplaySport = leaderboardSkiSport(category, skiSport);
  const withheldLabel = skiDisplaySport ? skiWithheldLabel(skiDisplaySport, entry) : null;
  // On a ski board a row closed for the season shows no number anywhere (owner decision: Closed
  // shows no number), whether the v3 status or the catalogue season window closed it.
  const skiSeasonClosed = skiDisplaySport !== null && entry.seasonClosed;
  const closedLabel = withheldLabel ?? (skiSeasonClosed ? SKI_LABEL_CLOSED : null);
  const skiV3 = skiDisplaySport !== null && hasSkiV3(entry);
  const conditionsLabel = isSupProfileKey(category)
    ? namedSupLeaderboardDisplayLabel(entry)
    : skiDisplaySport
      // One vocabulary per sport; the platform bands only pick the badge colour.
      ? skiLabel10(skiDisplaySport, displayScore)
      : conditionsBadge.label;
  const skiTooltip = skiV3 ? skiScoreTooltip(conditionsLabel, entry) : null;
  const skiRange = skiV3 ? skiLikelyRange(entry.lo, entry.hi) : null;
  // v3 scores keep the likely range in the tooltip; revision 2 rows keep the inline range.
  const reading = (score: number) =>
    skiV3 ? String(scoreOutOf100(score)) : scoreReading(score, depthConfidence);

  if (entry.isStale) {
    // A stale entry (no write within 5 hours, or the nightly gap: its row is the location's
    // previous local day) shows no number, as the spot page withholds a stale row's score; the
    // "Updating" chip and "Score unavailable" say why.
    return (
      <div className="bg-white/60 rounded-[16px] border border-border-light opacity-60">
        <Link
          href={entryHref}
          className="flex-1 min-w-0 text-left p-4 md:p-5 flex items-center gap-3 md:gap-5 no-underline"
        >
          <div className="flex items-center gap-2 md:gap-3 w-14 md:w-20 shrink-0">
            <span className="font-serif text-[22px] md:text-[26px] font-bold text-text-muted/40 tabular-nums">
              -
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-serif text-[17px] md:text-[19px] font-semibold text-text-muted leading-tight">
                {entry.name}
              </h3>
              <span className="text-[11px] text-text-muted/60">
                {entry.country}
              </span>
            </div>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-semibold text-text-muted">
                Updating
              </span>
            </div>
          </div>

          <div className="flex items-center shrink-0">
            <div className="rounded-[10px] px-2.5 py-1.5 text-center bg-slate-100 border border-slate-200">
              <div className="text-[13px] md:text-[14px] font-semibold leading-tight text-text-muted/60">
                {closedLabel ?? "Score unavailable"}
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  if (withheldLabel) {
    return (
      <SkiWithheldRow
        entry={entry}
        href={entryHref}
        label={withheldLabel}
      />
    );
  }

  if (skiSeasonClosed) {
    // A ski row closed for the season reads like a v3 Closed row, under the "Closed today"
    // heading or not: no number, the reason.
    return (
      <SkiWithheldRow
        entry={entry}
        href={entryHref}
        label={SKI_LABEL_CLOSED}
        reason={SKI_SEASON_CLOSED_REASON}
      />
    );
  }

  if (entry.seasonClosed) {
    // Non-ski boards only (MTB): the closed-for-season chip beside a muted score.
    return (
      <div className="bg-white/60 rounded-[16px] border border-border-light opacity-70">
        <Link
          href={entryHref}
          className="flex-1 min-w-0 text-left p-4 md:p-5 flex items-center gap-3 md:gap-5 no-underline"
        >
          <div className="flex items-center gap-2 md:gap-3 w-14 md:w-20 shrink-0">
            <span className="font-serif text-[22px] md:text-[26px] font-bold text-text-muted/40 tabular-nums">
              {entry.rank > 0 ? entry.rank : "-"}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-serif text-[17px] md:text-[19px] font-semibold text-text-muted leading-tight">
                {entry.name}
              </h3>
              <span className="text-[11px] text-text-muted/60">
                {entry.country}
              </span>
            </div>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-semibold text-text-muted">
                Closed for season
              </span>
            </div>
          </div>

          <div className="flex items-center shrink-0">
            <div className="rounded-[10px] px-2.5 py-1.5 text-center bg-slate-100 border border-slate-200">
              <div className="font-serif text-[18px] md:text-[22px] font-bold leading-none text-text-muted/40">
                {scoreOutOf100(displayScore)}
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  const luckBadge = luckBadgeColor(entry.luckDelta);
  const weatherCode = entry.weatherCode;

  return (
    <div
      className={`bg-white rounded-[16px] border transition-all ${
        theme === "alpine"
          ? "border-border-light hover:border-[rgba(75,163,195,0.25)] hover:shadow-[0_6px_24px_rgba(75,163,195,0.06)]"
          : theme === "coastal"
            ? "border-border-light hover:border-[rgba(13,148,136,0.25)] hover:shadow-[0_6px_24px_rgba(13,148,136,0.06)]"
            : "border-border-light hover:border-gold/25 hover:shadow-[0_6px_24px_rgba(0,0,0,0.05)]"
      }`}
    >
      <div className="flex items-center">
        {(category === "surf" || category === "ski") && onSelect && (
          <div className="pl-4 md:pl-5 shrink-0">
            <input
              type="checkbox"
              checked={selected ?? false}
              onChange={(e) => { e.stopPropagation(); onSelect(); }}
              disabled={!selected && disableCheckbox}
              className="w-4 h-4 rounded border-border-light text-accent focus:ring-accent/30 cursor-pointer disabled:opacity-40"
              aria-label={`Select ${entry.name} for comparison`}
            />
          </div>
        )}
        <Link
          href={entryHref}
          className="flex-1 min-w-0 text-left p-4 md:p-5 flex items-center gap-3 md:gap-5 no-underline"
        >
          <div className="flex items-center gap-2 md:gap-3 w-14 md:w-20 shrink-0">
            <span className="font-serif text-[22px] md:text-[26px] font-bold text-navy tabular-nums">
              {namedSafetyState === "flagged-red" ? "–" : entry.rank}
            </span>
            {mode === "luckiest" && (
              <MovementIndicator
                rank={entry.rank}
                previousRank={entry.previousRank}
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-serif text-[17px] md:text-[19px] font-semibold text-navy leading-tight">
                {entry.name}
              </h3>
              <span className="text-[11px] text-text-muted">
                {entry.country}
              </span>
              {namedSafetyState && namedSafetyState !== "clear" && (
                <span
                  className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
                    namedSafetyState === "flagged-red"
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : namedSafetyState === "flagged-amber"
                        ? "border-amber-200 bg-amber-50 text-amber-800"
                        : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  {namedSafetyState === "flagged-red"
                    ? "Red safety flag"
                    : namedSafetyState === "flagged-amber"
                      ? "Caution"
                      : "Safety evidence unavailable"}
                </span>
              )}
              {entry.powderAlert && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold leading-none">
                  Powder alert
                </span>
              )}
              {entry.busyPeriodLabel && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold leading-none">
                  {entry.busyPeriodLabel}
                </span>
              )}
              {skiV3 ? (
                <SkiClassChip fields={entry} label={conditionsLabel} />
              ) : depthConfidence && (
                <ScoreConfidenceBadge {...depthConfidence} />
              )}
              {skiV3 && skiDisplaySport === "piste" && (
                <SkiExtentChip extent={entry.extent} />
              )}
              {weatherCode != null && (
                <span
                  className="text-[14px] md:text-[15px] ml-auto md:ml-0"
                  aria-label={weatherCodeLabel(weatherCode)}
                  title={weatherCodeLabel(weatherCode)}
                >
                  {weatherCodeEmoji(weatherCode)}
                </span>
              )}
            </div>
            <div className="mt-1 hidden md:flex items-center gap-3">
              <RowStats category={category} skiSport={skiSport} entry={entry} />
              {mode === "luckiest" && entry.luckDelta !== null && entry.luckDelta !== 0 && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                    entry.luckDelta >= 1.5
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : entry.luckDelta >= 0.5
                        ? "bg-lime-50 text-lime-700 border border-lime-200"
                        : entry.luckDelta >= 0
                          ? "bg-amber-50 text-amber-700 border border-amber-200"
                          : "bg-orange-50 text-orange-700 border border-orange-200"
                  }`}
                >
                  {entry.luckDelta > 0 ? "+" : ""}{((entry.luckDelta / 5) * 100).toFixed(0)}% vs avg
                </span>
              )}
              {mode === "luckiest" && entry.luckDelta === null && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-50 text-text-muted border border-slate-200">
                  Not enough history
                </span>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center shrink-0">
            <TrendIndicator values={entry.history} />
          </div>

          <div className="flex items-center shrink-0">
            {mode === "conditions" ? (
              category === "ski" && entry.pisteScore != null && entry.offpisteScore != null ? (
                <div className="flex items-center gap-1.5">
                  {skiSport === "piste" ? (
                    <>
                      <div
                        className={`rounded-[10px] px-2 py-1.5 text-center ${alpineBadge ? alpineBadge.className : `${conditionsBadge.bg} ${conditionsBadge.text}`}`}
                        title={skiTooltip ?? undefined}
                      >
                        <div className="font-serif text-[16px] md:text-[19px] font-bold leading-none tabular-nums">
                          {entry.pisteScore != null
                            ? reading(entry.pisteScore)
                            : "–"}
                        </div>
                        <div className="text-[8px] uppercase tracking-[0.8px] font-semibold mt-0.5 opacity-90">
                          Piste
                        </div>
                        {skiRange && <span className="sr-only">{`. ${conditionsLabel}. ${skiRange}`}</span>}
                      </div>
                      <div className="hidden md:block rounded-[8px] px-1.5 py-1 text-center border border-slate-200 bg-transparent">
                        <div className="font-serif text-[13px] font-bold leading-none tabular-nums text-text-muted">
                          {entry.offpisteScore != null ? scoreOutOf100(entry.offpisteScore) : "–"}
                        </div>
                        <div className="text-[7px] uppercase tracking-[0.5px] font-medium mt-0.5 text-text-muted/70">
                          Off-piste
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {(() => {
                        const offpisteBadge = scoreBadgeColor(entry.offpisteScore ?? 0);
                        const offAlpine = theme === "alpine" ? alpineScoreBadge(entry.offpisteScore ?? 0) : theme === "coastal" ? coastalScoreBadge(entry.offpisteScore ?? 0) : null;
                        return (
                          <div className={`rounded-[10px] px-2 py-1.5 text-center ${offAlpine ? offAlpine.className : `${offpisteBadge.bg} ${offpisteBadge.text}`}`}>
                            <div className="font-serif text-[16px] md:text-[19px] font-bold leading-none tabular-nums">
                              {entry.offpisteScore != null
                                ? scoreReading(entry.offpisteScore, depthConfidence)
                                : "–"}
                            </div>
                            <div className="text-[8px] uppercase tracking-[0.8px] font-semibold mt-0.5 opacity-90">
                              Off-piste
                            </div>
                          </div>
                        );
                      })()}
                      <div className="hidden md:block rounded-[8px] px-1.5 py-1 text-center border border-slate-200 bg-transparent">
                        <div className="font-serif text-[13px] font-bold leading-none tabular-nums text-text-muted">
                          {entry.pisteScore != null ? scoreOutOf100(entry.pisteScore) : "–"}
                        </div>
                        <div className="text-[7px] uppercase tracking-[0.5px] font-medium mt-0.5 text-text-muted/70">
                          Piste
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
              <div
                className={`rounded-[10px] px-2.5 py-1.5 text-center ${alpineBadge ? alpineBadge.className : `${conditionsBadge.bg} ${conditionsBadge.text}`}`}
                title={skiTooltip ?? undefined}
              >
                <div className="font-serif text-[18px] md:text-[22px] font-bold leading-none tabular-nums">
                  {reading(entry.conditionsScore)}
                </div>
                <div className="text-[9px] uppercase tracking-[1px] font-semibold mt-0.5 opacity-90">
                  {conditionsLabel}
                </div>
                {skiRange && <span className="sr-only">{`. ${skiRange}`}</span>}
              </div>
              )
            ) : (
              <div
                className={`rounded-[10px] px-2.5 py-1.5 text-center ${luckBadge.bg} ${luckBadge.text}`}
              >
                {entry.luckDelta !== null ? (
                  <>
                    <div className="font-serif text-[18px] md:text-[22px] font-bold leading-none tabular-nums">
                      {entry.luckDelta >= 0 ? "+" : ""}{entry.luckDelta.toFixed(1)}
                    </div>
                    <div className="text-[9px] uppercase tracking-[1px] font-semibold mt-0.5 opacity-90">
                      Luck
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-serif text-[16px] md:text-[18px] font-bold leading-none">
                      N/A
                    </div>
                    <div className="text-[8px] uppercase tracking-[0.5px] font-medium mt-0.5">
                      Building
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Link>
        <div className="hidden shrink-0 pr-4 md:block">
          <SaveToCompanionButton
            id={entry.slug}
            spotType={saveSpotType}
            tone="compact"
            callPreference={callPreference}
          />
        </div>
      </div>

      <div className="md:hidden px-4 pb-3 -mt-1 flex flex-wrap items-center gap-3">
        <RowStats category={category} skiSport={skiSport} entry={entry} />
        {mode === "luckiest" && entry.luckDelta !== null && entry.luckDelta !== 0 && (
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
              entry.luckDelta >= 1.5
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : entry.luckDelta >= 0.5
                  ? "bg-lime-50 text-lime-700 border border-lime-200"
                  : entry.luckDelta >= 0
                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                    : "bg-orange-50 text-orange-700 border border-orange-200"
            }`}
          >
            {entry.luckDelta > 0 ? "+" : ""}{((entry.luckDelta / 5) * 100).toFixed(0)}%
          </span>
        )}
        <SaveToCompanionButton
          id={entry.slug}
          spotType={saveSpotType}
          tone="compact"
          callPreference={callPreference}
        />
      </div>
    </div>
  );
}

export type LeaderboardTheme = "default" | "alpine" | "coastal";

const BOARD_AREA_SPORT_LABELS: Readonly<Record<string, string>> = {
  piste: "Piste", "off-piste": "Off-piste", touring: "Touring", mtb: "MTB", surf: "Surf",
  bodyboarding: "Bodyboarding", wind: "Wind", foil: "Prone foil", "downwind-foil": "Downwind foil",
  "wing-foil": "Wing foil", diving: "Diving", swimming: "Open water", sup: "Paddle", sailing: "Sailing",
};

// Both helpers follow the shared platform bands (9/7.5/6.5/5/3.5/2) so the
// badge tint always agrees with the scoreBadgeColor() text label.
function alpineScoreBadge(score: number): { className: string; label: string } {
  if (score >= 9) return { className: "ski-badge-firing", label: "Hero" };
  if (score >= 7.5) return { className: "ski-badge-firing", label: "Firing" };
  if (score >= 6.5) return { className: "ski-badge-great", label: "Great" };
  if (score >= 5) return { className: "ski-badge-good", label: "Good" };
  if (score >= 3.5) return { className: "ski-badge-fair", label: "Fair" };
  if (score >= 2) return { className: "ski-badge-fair", label: "Poor" };
  return { className: "ski-badge-flat", label: "Flat" };
}

function coastalScoreBadge(score: number): { className: string; label: string } {
  if (score >= 9) return { className: "bg-teal-700 text-white", label: "Hero" };
  if (score >= 7.5) return { className: "bg-teal-600 text-white", label: "Firing" };
  if (score >= 6.5) return { className: "bg-teal-500 text-white", label: "Great" };
  if (score >= 5) return { className: "bg-teal-400/80 text-white", label: "Good" };
  if (score >= 3.5) return { className: "bg-teal-300/60 text-teal-900", label: "Fair" };
  if (score >= 2) return { className: "bg-orange-200 text-orange-900", label: "Poor" };
  return { className: "bg-slate-200 text-slate-600", label: "Flat" };
}

// Sport lens for each board's links into the shared spot page. Surf is the
// coastal default and sailing has its own page, so neither needs a lens.
const BOARD_LENS_SPORT: Partial<Record<LeaderboardCategory, string>> = {
  touring: "touring",
  wind: "wind",
  foil: "foil",
  "downwind-foil": "downwind-foil",
  "wing-foil": "wing-foil",
  diving: "diving",
  sup: "sup-touring",
  "sup-touring": "sup-touring",
  "sup-surf": "sup-surf",
  "sup-downwind": "sup-downwind",
  swim: "swimming",
  mtb: "mtb",
  bodyboarding: "bodyboarding",
};

function boardLensSport(category: LeaderboardCategory, skiSport: SkiSport): string | null {
  if (category === "ski") return skiSport === "offpiste" ? "off-piste" : skiSport;
  return BOARD_LENS_SPORT[category] ?? null;
}

// Every board links to canonical spot URLs. The retired per-sport paths
// (/conditions/wind/<slug> and friends) only redirect.
function defaultBasePath(category: LeaderboardCategory): string {
  return category === "sailing" ? "/conditions/sailing" : "/conditions";
}

export function LeaderboardTable({
  category,
  entries: publishedEntries,
  skiSport = "piste",
  theme = "default",
  basePath: basePathProp,
  areas = null,
}: {
  category: LeaderboardCategory;
  entries: LeaderboardEntry[];
  skiSport?: SkiSport;
  theme?: LeaderboardTheme;
  basePath?: string;
  /**
   * Forecast area features (chips, area filter, This week view, Near you, off-season banner),
   * built on the server by `boardAreaProps`. Off by default, so the named paddle boards and any
   * other caller render exactly as before.
   */
  areas?: BoardAreaProps | null;
}) {
  // The board pages are statically rendered (rebuilt at most every 3 hours), so the server judged
  // each entry's freshness at its render. After hydration the browser's minute clock judges again
  // (board-freshness.ts): an entry whose location has passed local midnight, or whose row is now
  // 5 hours old, joins the "Updating" rows and leaves the ranking and podium.
  const browserNowMs = useBrowserMinuteClock();
  const entries = useMemo((): LeaderboardEntry[] => {
    if (browserNowMs === null) return publishedEntries;
    const judged = boardEntriesAt(publishedEntries, browserNowMs);
    return judged === publishedEntries ? publishedEntries : [...judged];
  }, [publishedEntries, browserNowMs]);
  const basePath = basePathProp ?? defaultBasePath(category);
  // Post-hydration param reads: these leaderboard pages are statically
  // rendered, and useSearchParams() would replace the server HTML with the
  // Suspense fallback. The default (unfiltered) table is in the static HTML
  // and URL filters apply after hydration.
  const boatParam = useShallowSearchParam("boat");
  const regionParamRaw = useShallowSearchParam("region");
  const countryParamRaw = useShallowSearchParam("country");
  const difficultyParamRaw = useShallowSearchParam("difficulty");
  const areaParamRaw = useShallowSearchParam("area");
  const viewParamRaw = useShallowSearchParam("view");
  // Area features apply only when the server passed them (never on the named paddle boards).
  const areaIndex = areas?.index ?? null;
  const activeArea = useMemo(
    () => (areaIndex ? knownBoardArea(areaIndex, areaParamRaw) : null),
    [areaIndex, areaParamRaw],
  );
  const areaEntries = useMemo(
    () => (areaIndex && activeArea ? [...filterByArea(areaIndex, entries, activeArea)] : entries),
    [areaIndex, entries, activeArea],
  );
  const activeAreaName = activeArea && areaIndex ? areaIndex.areaNames[activeArea] ?? null : null;
  const weekView = Boolean(areas?.weekAvailable) && viewParamRaw === "week";
  // Params other filters must carry when they rewrite the query string.
  const keptParams = {
    boat: boatParam,
    area: activeArea,
    view: weekView ? "week" : null,
  };

  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [mode, setMode] = useState<LeaderboardMode>("conditions");
  const [searchQuery, setSearchQuery] = useState("");
  const sailingBoat: SailingBoat = boatParam === "hp" ? "hp" : "club";

  const regionParam = regionParamRaw;
  const countryParam = countryParamRaw?.toLowerCase() ?? null;
  const difficultyParam = category === "surf" ? difficultyParamRaw : null;
  const activeDifficulty: "all" | "intermediate" | "advanced" =
    difficultyParam === "intermediate" || difficultyParam === "advanced"
      ? difficultyParam
      : "all";

  const activeRegion = useMemo(() => {
    if (regionParam) return regionParam;
    if (countryParam) {
      const entry = entries.find(
        (e) => e.countryCode.toLowerCase() === countryParam
      );
      if (entry) return getContinentId(entry.country);
    }
    return null;
  }, [regionParam, countryParam, entries]);

  const availableContinents = useMemo(
    () => getAvailableContinents(entries),
    [entries]
  );

  const availableCountries = useMemo(
    () => getCountriesForEntries(entries, activeRegion),
    [entries, activeRegion]
  );

  const filteredEntries = useMemo(() => {
    let result = areaEntries;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.country.toLowerCase().includes(q)
      );
    }
    if (countryParam) {
      result = result.filter(
        (e) => e.countryCode.toLowerCase() === countryParam
      );
    } else if (activeRegion) {
      result = result.filter((e) => getContinentId(e.country) === activeRegion);
    }
    if (activeDifficulty === "intermediate") {
      result = result.filter(
        (e) =>
          e.difficultyLevel === "intermediate" ||
          e.difficultyLevel === "advanced" ||
          e.difficultyLevel === "expert"
      );
    } else if (activeDifficulty === "advanced") {
      result = result.filter(
        (e) =>
          e.difficultyLevel === "advanced" || e.difficultyLevel === "expert"
      );
    }
    return result;
  }, [areaEntries, activeRegion, countryParam, activeDifficulty, searchQuery]);

  const boardSkiSport = leaderboardSkiSport(category, skiSport);

  const rankedBoard = useMemo(() => {
    // A withheld ski score (Closed, or No lift access) is never ranked: it joins the closed
    // group at the bottom of the board, with no number.
    const skiWithheld = (e: LeaderboardEntry) =>
      boardSkiSport !== null && isSkiWithheld(boardSkiSport, e);
    const fresh = filteredEntries.filter((e) => !e.isStale && !e.seasonClosed && !skiWithheld(e));
    const closed = filteredEntries.filter((e) => (e.seasonClosed || skiWithheld(e)) && !e.isStale);
    const stale = filteredEntries.filter((e) => e.isStale);
    const sortFresh = (source: LeaderboardEntry[]) => {
      const copy = [...source];
      if (mode === "conditions") {
        if (category === "ski" && skiSport === "offpiste") {
          copy.sort((a, b) => (b.offpisteScore ?? b.conditionsScore) - (a.offpisteScore ?? a.conditionsScore));
        } else if (category === "ski" && skiSport === "piste") {
          copy.sort((a, b) => (b.pisteScore ?? b.conditionsScore) - (a.pisteScore ?? a.conditionsScore));
        } else if (category === "touring") {
          copy.sort((a, b) => (b.touringScore ?? b.conditionsScore) - (a.touringScore ?? a.conditionsScore));
        } else if (category === "sailing" && sailingBoat === "hp") {
          copy.sort((a, b) => (b.sailingHpScore ?? 0) - (a.sailingHpScore ?? 0));
        } else {
          copy.sort((a, b) => b.conditionsScore - a.conditionsScore);
        }
      } else {
        copy.sort((a, b) => (b.luckDelta ?? -Infinity) - (a.luckDelta ?? -Infinity));
      }
      return copy;
    };
    const eligibleFresh = isSupProfileKey(category)
      ? fresh.filter(isNamedSupLeaderboardPromotionEligible)
      : fresh;
    const safetyHeld = isSupProfileKey(category)
      ? fresh.filter((entry) => !isNamedSupLeaderboardPromotionEligible(entry))
      : [];
    const copy = sortFresh(eligibleFresh);
    const ranked = copy.map((e, i) => ({ ...e, rank: i + 1 }));
    const held = sortFresh(safetyHeld).map((entry) => ({ ...entry, rank: 0 }));
    const closedAlpha = [...closed]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((e) => ({ ...e, rank: 0 }));
    const staleByScore = [...stale]
      .sort((a, b) => b.conditionsScore - a.conditionsScore)
      .map((e) => ({ ...e, rank: 0 }));
    // The "Closed today" heading appears only when a ski score is withheld (v3 rows). The
    // closed group is then the last block, so the stale "Updating" rows move above it;
    // without the heading the order is today's.
    const closedHeading = closed.some(skiWithheld);
    return {
      closedHeading,
      rows: closedHeading
        ? [...ranked, ...held, ...staleByScore, ...closedAlpha]
        : [...ranked, ...held, ...closedAlpha, ...staleByScore],
    };
  }, [filteredEntries, mode, category, skiSport, sailingBoat, boardSkiSport]);
  const rankedEntries = rankedBoard.rows;

  const entryIsSkiWithheld = (entry: LeaderboardEntry) =>
    boardSkiSport !== null && isSkiWithheld(boardSkiSport, entry);
  const entryInClosedGroup = (entry: LeaderboardEntry) =>
    !entry.isStale && (entry.seasonClosed || entryIsSkiWithheld(entry));
  const firstClosedSlug = rankedBoard.closedHeading
    ? rankedEntries.find(entryInClosedGroup)?.slug ?? null
    : null;
  // "Ranked" counts leave out withheld scores, like the page hero (openSkiEntries).
  const withheldShown = boardSkiSport === null ? 0 : filteredEntries.filter(entryIsSkiWithheld).length;

  const podiumEntries = rankedEntries.filter((entry) =>
    !entry.isStale && !entry.seasonClosed && entry.rank > 0 && !entryIsSkiWithheld(entry) &&
    ((category !== "ski" && category !== "touring") || skiScoreConfidenceIsPublishable(snowDepthConfidence(entry, category, skiSport)?.confidence)) &&
    (category !== "sailing" || sailingBoat !== "hp" || entry.sailingHpScore !== null) &&
    (!isSupProfileKey(category) || isNamedSupLeaderboardPromotionEligible(entry))
  ).slice(0, 3).map((entry) => category === "sailing" && sailingBoat === "hp" ? { ...entry, conditionsScore: entry.sailingHpScore! } : entry);

  const topEntry = rankedEntries.find((entry) =>
    !entry.isStale &&
    !entry.seasonClosed &&
    !entryIsSkiWithheld(entry) &&
    (!isSupProfileKey(category) || isNamedSupLeaderboardPromotionEligible(entry))
  );
  const isFiltered = Boolean(activeArea || activeRegion || countryParam || activeDifficulty !== "all" || searchQuery.trim());

  const activeCountryName = countryParam
    ? availableCountries.find((c) => c.code === countryParam)?.name ?? null
    : null;
  const activeContinentLabel = activeRegion
    ? getContinentLabel(activeRegion)
    : null;
  const filterLabel = activeCountryName ?? activeContinentLabel ?? activeAreaName;

  function handleRegionChange(id: string | null) {
    setShallowSearchParams(
      {
        ...keptParams,
        // A continent replaces an area filter.
        area: null,
        region: id,
        difficulty: activeDifficulty !== "all" ? activeDifficulty : null,
      },
      { mode: "replace" }
    );
  }

  function handleCountryChange(code: string | null) {
    let region: string | null = null;
    if (code) {
      const entry = entries.find(
        (e) => e.countryCode.toLowerCase() === code
      );
      region = (entry ? getContinentId(entry.country) : activeRegion) ?? null;
    } else if (activeRegion) {
      region = activeRegion;
    }
    setShallowSearchParams(
      {
        ...keptParams,
        area: null,
        region,
        country: code,
        difficulty: activeDifficulty !== "all" ? activeDifficulty : null,
      },
      { mode: "replace" }
    );
  }

  function handleDifficultyChange(level: "all" | "intermediate" | "advanced") {
    setShallowSearchParams(
      {
        ...keptParams,
        region: regionParam,
        country: countryParam,
        difficulty: level !== "all" ? level : null,
      },
      { mode: "replace" }
    );
  }

  /** Area filter: replaces continent and country filters; keeps the week view and boat. */
  function handleAreaChange(slug: string | null) {
    setShallowSearchParams(
      {
        area: slug,
        region: null,
        country: null,
        view: keptParams.view,
      },
      { mode: "merge" }
    );
  }

  function handleViewChange(next: "today" | "week") {
    setShallowSearchParams({ view: next === "week" ? "week" : null }, { mode: "merge" });
  }

  useEffect(() => {
    queueMicrotask(() => {
      setSelectedSlugs([]);
    });
  }, [regionParam, countryParam, difficultyParam, activeArea]);

  useEffect(() => {
    const sportNames: Record<string, string> = { ski: "Skiing", wind: "Wind Sports", swim: "Swimming", foil: "Prone Foil", "downwind-foil": "Downwind Foil", "wing-foil": "Wing Foil", diving: "Diving", sup: "SUP", surf: "Surf", mtb: "Mountain Biking", sailing: "Sailing", bodyboarding: "Bodyboarding" };
    const sport = sportNames[category] ?? "Surf";
    if (activeAreaName) {
      document.title = `Best ${sport} in ${activeAreaName} Today | PeakWave`;
    } else if (activeCountryName) {
      document.title = `Best ${sport} in ${activeCountryName} Today | PeakWave`;
    } else if (activeContinentLabel) {
      document.title = `Best ${sport} in ${activeContinentLabel} Today | PeakWave`;
    } else {
      const verbNames: Record<string, string> = { ski: "Ski", wind: "Kite", swim: "Swim", foil: "Foil", "downwind-foil": "Downwind Foil", "wing-foil": "Wing Foil", diving: "Dive", sup: "Paddle", surf: "Surf", mtb: "Ride", sailing: "Sail", bodyboarding: "Bodyboard" };
      const verb = verbNames[category] ?? "Surf";
      document.title = `Best Place to ${verb} Today | Live ${sport} Conditions | PeakWave`;
    }
  }, [activeAreaName, activeCountryName, activeContinentLabel, category]);

  const toggleSlug = (slug: string) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug)
        ? prev.filter((s) => s !== slug)
        : prev.length >= 4
          ? prev
          : [...prev, slug]
    );
  };

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-[14px] border border-border-light p-8 text-center">
        <p className="text-[15px] text-text-muted">
          No data available yet. The leaderboard refreshes daily.
        </p>
      </div>
    );
  }

  const pillOn = theme === "alpine"
    ? "bg-[#4BA3C3] text-white border border-[#4BA3C3]"
    : theme === "coastal"
      ? "bg-[#0D9488] text-white border border-[#0D9488]"
      : "bg-gold text-white border border-gold";
  const pillOff = theme === "alpine"
    ? "bg-white border border-border-light text-text-body hover:border-[#4BA3C3]/50 hover:text-[#4BA3C3]"
    : theme === "coastal"
      ? "bg-white border border-border-light text-text-body hover:border-[#0D9488]/50 hover:text-[#0D9488]"
      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold";
  const areaSportLabel = areas ? BOARD_AREA_SPORT_LABELS[areas.sport] ?? "Conditions" : "Conditions";

  return (
    <div className="space-y-2.5 md:space-y-3">
      {areas?.offSeason && <BoardOffSeasonBanner offSeason={areas.offSeason} />}

      {areas?.weekAvailable && (
        <div className="flex items-center gap-2" role="radiogroup" aria-label="Time range" data-board-view-toggle>
          {(
            [
              { id: "today", label: "Today" },
              { id: "week", label: "This week" },
            ] as const
          ).map((opt) => {
            const checked = (opt.id === "week") === weekView;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => handleViewChange(opt.id)}
                className={`min-h-11 px-4 py-1.5 rounded-full text-[12.5px] font-semibold cursor-pointer font-sans transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal ${checked ? pillOn : pillOff}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {areas && areas.chipMode === "links" && (
        <BoardAreaChips
          chips={areas.chips}
          allAreasHref={areas.allAreasHref}
          sportLabel={areas.sport === "piste" ? "Snow" : areaSportLabel}
        />
      )}
      {areas && areas.chipMode === "filter" && (
        <BoardAreaFilter
          chips={areas.chips}
          activeArea={activeArea}
          activeAreaName={activeAreaName}
          onSelect={handleAreaChange}
        />
      )}
      {areas?.nearYou && (
        <BoardNearYouChip
          sports={[{ sport: areas.sport, label: areaSportLabel }]}
          onSelectArea={areas.chipMode === "filter" ? handleAreaChange : undefined}
          knownArea={areas.chipMode === "filter" ? (slug) => knownBoardArea(areas.index, slug) !== null : undefined}
          activeArea={activeArea}
        />
      )}

      {weekView && areas ? (
        <BoardWeekView
          sport={areas.sport}
          lensSport={boardLensSport(category, skiSport)}
          index={areaIndex}
          activeArea={activeArea}
          activeAreaName={activeAreaName}
          groupByAreaAvailable={areas.weekGroupByArea}
        />
      ) : (
      <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
        <div className="flex items-center gap-2" role="radiogroup" aria-label="Ranking mode">
          {(
            [
              { id: "conditions", label: "Best Conditions" },
              { id: "luckiest", label: "Luckiest" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={mode === opt.id}
              onClick={() => setMode(opt.id)}
              className={`min-h-11 px-4 py-1.5 rounded-full text-[12.5px] font-medium cursor-pointer font-sans transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
                mode === opt.id
                  ? theme === "alpine"
                    ? "bg-[#4BA3C3] text-white border border-[#4BA3C3]"
                    : theme === "coastal"
                      ? "bg-[#0D9488] text-white border border-[#0D9488]"
                      : "bg-gold text-white border border-gold"
                  : theme === "alpine"
                    ? "bg-white border border-border-light text-text-body hover:border-[#4BA3C3]/50 hover:text-[#4BA3C3]"
                    : theme === "coastal"
                      ? "bg-white border border-border-light text-text-body hover:border-[#0D9488]/50 hover:text-[#0D9488]"
                      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-text-muted">
          {mode === "conditions"
            ? "Ranked by conditions score"
            : "Ranked by luck factor"}
        </span>
      </div>

      <div className="relative">
        <svg
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted/60"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
        >
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search locations..."
          className="min-h-11 w-full bg-white border border-border-light rounded-full pl-10 pr-12 py-2.5 text-[13px] text-text-body placeholder:text-text-muted/50 focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold transition-all"
          aria-label="Search locations by name"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-1 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center text-text-muted/60 hover:text-text-body transition-colors focus-visible:ring-2 focus-visible:ring-teal"
            aria-label="Clear search"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>


      {category === "sailing" && (
        <div className="flex items-center gap-2" role="radiogroup" aria-label="Boat category">
          {(
            [
              { id: "club", label: "Club Dinghy" },
              { id: "hp", label: "High Performance" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={sailingBoat === opt.id}
              onClick={() => {
                setShallowSearchParams({
                  boat: opt.id === "hp" ? "hp" : null,
                });
              }}
              className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium cursor-pointer font-sans transition-all ${
                sailingBoat === opt.id
                  ? "bg-navy text-white border border-navy"
                  : "bg-white border border-border-light text-text-body hover:border-navy/40 hover:text-navy"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {category === "surf" && (
        <div className="flex items-center gap-2" role="radiogroup" aria-label="Difficulty filter">
          {(
            [
              { id: "all", label: "All Spots" },
              { id: "intermediate", label: "Intermediate+" },
              { id: "advanced", label: "Advanced" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={activeDifficulty === opt.id}
              onClick={() => handleDifficultyChange(opt.id)}
              className={`min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal px-3.5 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap cursor-pointer transition-all ${
                activeDifficulty === opt.id
                  ? "bg-navy text-white border border-navy"
                  : "bg-white border border-border-light text-text-body hover:border-navy/40 hover:text-navy"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 overflow-x-auto pb-1" role="radiogroup" aria-label="Region filter">
        <button
          type="button"
          role="radio"
          aria-checked={!activeRegion}
          onClick={() => handleRegionChange(null)}
          className={`min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal px-3.5 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap cursor-pointer transition-all ${
            !activeRegion
              ? "bg-navy text-white border border-navy"
              : "bg-white border border-border-light text-text-body hover:border-navy/40 hover:text-navy"
          }`}
        >
          All
        </button>
        {availableContinents.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={activeRegion === c.id && !countryParam}
            onClick={() => handleRegionChange(c.id)}
            className={`min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal px-3.5 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap cursor-pointer transition-all ${
              activeRegion === c.id
                ? "bg-navy text-white border border-navy"
                : "bg-white border border-border-light text-text-body hover:border-navy/40 hover:text-navy"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <select
          value={countryParam ?? ""}
          onChange={(e) => handleCountryChange(e.target.value || null)}
          className="min-h-11 min-w-0 bg-white border border-border-light rounded-full px-4 py-1.5 text-[12.5px] text-text-body cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold"
          aria-label="Filter by country"
        >
          <option value="">All countries</option>
          {availableCountries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        <span className="text-[12px] text-text-muted whitespace-nowrap">
          {filterLabel ? filteredEntries.length : filteredEntries.length - withheldShown}
          {isFiltered ? ` of ${entries.length}` : ""} locations
          {filterLabel ? ` in ${filterLabel}` : " ranked"}
          {withheldShown > 0 ? `, ${withheldShown} closed` : ""}
        </span>
      </div>

      {mode === "conditions" && <PodiumSection entries={podiumEntries} category={category === "ski" ? skiSport : category} theme={theme === "alpine" ? "alpine" : "coastal"} basePath={basePath} compact title={searchQuery.trim() || isFiltered ? "Top conditions matching your filters" : "Today’s top conditions"} />}

      {mode === "luckiest" && topEntry && theme !== "alpine" && (
        <div className="inline-flex items-center gap-3 bg-white border border-border-light rounded-full py-2 pl-2 pr-5">
          <span className="w-7 h-7 rounded-full bg-gold text-white font-bold text-sm flex items-center justify-center">
            1
          </span>
          <span className="text-[13px] text-text-body">
            Today&apos;s number one{filterLabel ? ` in ${filterLabel}` : ""}:{" "}
            <strong className="font-semibold text-navy">{topEntry.name}</strong>{" "}
            {topEntry.luckDelta !== null ? (
              <>
                at{" "}
                <strong className="font-semibold text-navy">
                  {topEntry.luckDelta >= 0 ? "+" : ""}
                  {topEntry.luckDelta.toFixed(1)}
                </strong>{" "}
                vs average
              </>
            ) : (
              <span className="text-text-muted">luck data building</span>
            )}
          </span>
        </div>
      )}

      {rankedEntries.length === 0 && isFiltered && (
        <div className="bg-white rounded-[14px] border border-border-light p-8 text-center">
          <p className="text-[15px] text-text-muted">
            No locations found{filterLabel ? ` in ${filterLabel}` : ""}. Try a
            different filter.
          </p>
        </div>
      )}

      {rankedEntries.map((entry) => (
        <Fragment key={entry.slug}>
          {entry.slug === firstClosedSlug && (
            <div className="pt-3 md:pt-4" data-ski-closed-group>
              <h2 className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-body">
                Closed today
              </h2>
              <p className="text-[12px] text-slate-600 mt-0.5">
                Not ranked. A closed resort shows no score.
              </p>
            </div>
          )}
          <LeaderboardRow
            entry={entry}
            category={category}
            mode={mode}
            skiSport={skiSport}
            sailingBoat={sailingBoat}
            theme={theme}
            basePath={basePath}
            selected={selectedSlugs.includes(entry.slug)}
            onSelect={() => toggleSlug(entry.slug)}
            disableCheckbox={selectedSlugs.length >= 4}
          />
        </Fragment>
      ))}
      {(category === "surf" || category === "ski") && selectedSlugs.length >= 2 && (
        <div className="sticky bottom-4 mx-auto max-w-fit bg-navy text-white rounded-full px-5 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.15)] flex items-center gap-3 z-20">
          <span className="text-[13px]">
            {selectedSlugs.length} {category === "ski" ? "resorts" : "spots"} selected
          </span>
          <Link
            href={`/conditions/compare?spots=${selectedSlugs.join(",")}${category === "ski" ? "&sport=piste" : ""}`}
            className="inline-flex items-center gap-1.5 bg-white text-navy text-[13px] font-semibold px-4 py-1.5 rounded-full no-underline hover:bg-white/90 transition-colors"
          >
            Compare
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 10l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      )}
      </>
      )}
    </div>
  );
}
